import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@mui/material';
import AppLoader from '../../../components/AppLoader/AppLoader';
import DescriptionIcon from '@mui/icons-material/Description';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import MrcElectricityConsumerBill from '../../../components/ElectricityConsumerBill/MrcElectricityConsumerBill';
import { fetchApprovedMrcBill } from '../../../components/ElectricityConsumerBill/fetchApprovedMrcBill';
import { mapApprovedMrcBillResponse } from '../../../components/ElectricityConsumerBill/mapApprovedMrcBillResponse';
import { MRC_BILL_CP52_FORM_KEY } from '../../../components/ElectricityConsumerBill/openMrcBillTab';
import { dummyAdvancePaymentBillData } from '../../../components/ElectricityConsumerBill/dummyAdvancePaymentBillData';
import { getDiscoBillBranding, getLogoColorLight } from '../../../components/ElectricityConsumerBill/discoBillBranding';
import { cfpBaseUrl } from '../../../network/urls';
import { enrichCp52BaseDataFromDocuments } from './mrcReadingUtils';
import {
    computeMrcBillQrAmount,
    consumeStashedMrcBillPaymentQr,
    fetchMrcBillPaymentQr,
    parseMrcBillNumber,
} from '../../../components/ElectricityConsumerBill/fetchMrcBillPaymentQr';
import { unwrapMrcCp52Record, expandMrcBillMeterImages } from './mrcCp52Api';
import '../AdvancePayment/AdvancePaymentBillView.scss';

const BILL_NODE_ID = 'mrc-consumer-bill';

const buildEmptyMrcBill = (applicationId) => ({
    ...dummyAdvancePaymentBillData,
    ...getDiscoBillBranding(null),
    applicationId: applicationId || null,
    billMessagesLayoutOnly: true,
    hideHeaderUrduText: false,
    hideQrContent: false,
});

const MeterReadingCorrectionBillView = () => {
    const { token } = useSelector((state) => state.auth);
    const [searchParams] = useSearchParams();
    const applicationId = searchParams.get('applicationId');
    const oracleTrackingId = searchParams.get('oracleTrackingId') || '';
    const isPostedToIPS = searchParams.get('isPostedToIPS') === 'true';
    const applicationPayableWithinDueDate =
        searchParams.get('payableWithinDueDate') || '';
    const customerIdFromQuery = searchParams.get('customerId') || '';
    const [billData, setBillData] = useState(() => buildEmptyMrcBill(applicationId));
    const [loading, setLoading] = useState(Boolean(applicationId && token));
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState('');
    const [loadError, setLoadError] = useState('');
    const isDownloadingRef = useRef(false);

    const getBillFileName = useCallback(() => {
        const trackingId = billData?.applicationId || applicationId;
        return trackingId ? String(trackingId).trim() : 'mrc-bill';
    }, [billData?.applicationId, applicationId]);

    const handleDownloadPdf = useCallback(async () => {
        const billNode = document.getElementById(BILL_NODE_ID);
        if (!billNode || isDownloadingRef.current) return;

        isDownloadingRef.current = true;
        setDownloading(true);
        setDownloadError('');

        try {
            const canvas = await html2canvas(billNode, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: false,
                width: billNode.scrollWidth,
                height: billNode.scrollHeight,
                onclone: (clonedDoc) => {
                    const clonedBill = clonedDoc.getElementById(BILL_NODE_ID);
                    if (!clonedBill) return;

                    clonedBill.style.overflow = 'visible';
                    const logoColor =
                        clonedBill.style.getPropertyValue('--bill-logo-color') || '#1b6b38';
                    clonedBill.style.setProperty(
                        '--bill-logo-color-light',
                        getLogoColorLight(logoColor)
                    );
                },
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.98);
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: true,
            });

            const pageWidth = 210;
            const pageHeight = 297;
            const renderedHeight = (canvas.height * pageWidth) / canvas.width;

            if (renderedHeight <= pageHeight) {
                pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, renderedHeight);
            } else {
                const renderedWidth = (canvas.width * pageHeight) / canvas.height;
                pdf.addImage(imgData, 'JPEG', 0, 0, renderedWidth, pageHeight);
            }

            pdf.save(`${getBillFileName()}.pdf`);
        } catch (downloadErr) {
            setDownloadError('Failed to download PDF. Please try Print Bill or refresh the page.');
        } finally {
            isDownloadingRef.current = false;
            setDownloading(false);
        }
    }, [getBillFileName]);

    const handlePrintBill = useCallback(() => {
        const billNode = document.getElementById(BILL_NODE_ID);
        if (!billNode) return;

        const printFrame = document.createElement('iframe');
        printFrame.setAttribute('aria-hidden', 'true');
        printFrame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
        document.body.appendChild(printFrame);

        const printDocument = printFrame.contentWindow?.document;
        if (!printDocument) {
            document.body.removeChild(printFrame);
            return;
        }

        const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
            .map((node) => node.outerHTML)
            .join('');
        const billFileName = getBillFileName();

        printDocument.open();
        printDocument.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <title>${billFileName}</title>
                    <style>
                        @page {
                            size: A4 portrait;
                            margin: 0;
                        }
                        html, body {
                            margin: 0 !important;
                            padding: 0 !important;
                            width: 210mm !important;
                            height: 297mm !important;
                            max-height: 297mm !important;
                            overflow: hidden !important;
                            background: #ffffff !important;
                        }
                        @media print {
                            *, *::before, *::after {
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                                color-adjust: exact !important;
                            }
                        }
                    </style>
                    ${styles}
                </head>
                <body>${billNode.outerHTML}</body>
            </html>
        `);
        printDocument.close();

        const cleanup = () => {
            if (printFrame.parentNode) {
                printFrame.parentNode.removeChild(printFrame);
            }
        };

        printFrame.contentWindow.onload = () => {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
            setTimeout(cleanup, 1000);
        };
    }, [getBillFileName]);

    useEffect(() => {
        setBillData(buildEmptyMrcBill(applicationId));
        setLoadError('');

        if (!token) {
            setLoadError('Session expired. Please sign in again.');
            setLoading(false);
            return undefined;
        }

        // Temporary hard-coded getCP52 params (same record CP52 uses in testing).
        const resolvedApplicationId =
            applicationId || 'MRC-131422608100003';
        const resolvedOracleTrackingId =
            oracleTrackingId || '3247844';

        let isMounted = true;

        const loadBill = async () => {
            setLoading(true);
            setLoadError('');

            // 1) Use QR already fetched on table click (same DN API).
            let raastQr = consumeStashedMrcBillPaymentQr();

            try {
                // Same API as CP52 dialog: mrc/getCP52
                const response = await fetchApprovedMrcBill(
                    {
                        applicationId: resolvedApplicationId,
                        oracleTrackingId: resolvedOracleTrackingId,
                        isPostedToIPS,
                    },
                    token
                );

                // Prefer CP52 form already prepared in dialog (has meter readings).
                let cp52Base = null;
                let meterFactor = null;
                let documents = [];
                try {
                    const stashed = localStorage.getItem(MRC_BILL_CP52_FORM_KEY);
                    if (stashed) {
                        cp52Base = JSON.parse(stashed);
                        localStorage.removeItem(MRC_BILL_CP52_FORM_KEY);
                    }
                } catch (e) {
                    cp52Base = null;
                }

                // Always load documents so all meter sets show on bill (like CP52).
                try {
                    const docsRes = await fetch(`${cfpBaseUrl}mrc/getMRCDocuments`, {
                        method: 'POST',
                        headers: {
                            Authorization: token,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            applicationId: resolvedApplicationId,
                        }),
                        redirect: 'follow',
                    });
                    const docsJson = await docsRes.json();
                    documents =
                        docsJson.status == '200' && Array.isArray(docsJson.data)
                            ? docsJson.data
                            : [];
                } catch (e) {
                    documents = [];
                }

                if (!cp52Base) {
                    cp52Base = enrichCp52BaseDataFromDocuments({}, documents);
                }

                let tariffs = [];
                try {
                    const tariffRes = await fetch(
                        `${cfpBaseUrl}customerFaciliationPortal/getTariffsDetail`,
                        {
                            method: 'POST',
                            headers: { Authorization: token },
                            redirect: 'follow',
                        }
                    );
                    const tariffJson = await tariffRes.json();
                    tariffs =
                        tariffJson?.data && Array.isArray(tariffJson.data)
                            ? tariffJson.data
                            : [];
                } catch (e) {
                    tariffs = [];
                }

                const primary = documents.find(
                    (doc) =>
                        Number(doc.meterId ?? doc.METER_ID) === 1 ||
                        Number(doc.meterID ?? doc.METER_ID) === 1
                ) || documents[0];
                meterFactor =
                    cp52Base?.mf ??
                    cp52Base?.meterFactor ??
                    cp52Base?.MF ??
                    primary?.mf ??
                    primary?.MF ??
                    primary?.meterFactor ??
                    null;

                // Bill payable = application payableWithinDueDate − getCP52 AMOUNT
                const mappedBillData = mapApprovedMrcBillResponse(response, {
                    applicationId: resolvedApplicationId,
                    base: {
                        ...(cp52Base || {}),
                        customerId:
                            customerIdFromQuery ||
                            cp52Base?.customerId ||
                            cp52Base?.consumerId ||
                            '',
                    },
                    meterFactor,
                    documents,
                    payableWithinDueDate: applicationPayableWithinDueDate,
                    customerId: customerIdFromQuery,
                    tariffs,
                });

                if (!mappedBillData) {
                    if (!isMounted) return;
                    setBillData(null);
                    setLoadError('No record found for the specified tracking ID.');
                    return;
                }

                // Refresh QR with computed payable (PType = 1).
                try {
                    const cp52Row = unwrapMrcCp52Record(response) || {};
                    const payableAmount = parseMrcBillNumber(
                        mappedBillData.payableWithinDueDate ||
                            mappedBillData.grandTotal ||
                            0
                    );
                    if (mappedBillData.hideQrContent || payableAmount < 0) {
                        raastQr = null;
                    } else {
                        const qrAmount = computeMrcBillQrAmount(
                            applicationPayableWithinDueDate ||
                                mappedBillData.applicationPayableWithinDueDate,
                            cp52Row.AMOUNT ?? cp52Row.CALCULATED_AMOUNT
                        );
                        const refNo = String(
                            cp52Row.REFERENCE_NO || mappedBillData.referenceNo || ''
                        )
                            .replace(/\s/g, '')
                            .trim();
                        const billNo = refNo;

                        raastQr =
                            (await fetchMrcBillPaymentQr({
                                token,
                                billNo,
                                appNo: resolvedApplicationId,
                                refNo,
                                amount: qrAmount || Math.abs(payableAmount),
                                dueDate: cp52Row.DUE_DT || mappedBillData.dueDate || '',
                                bPeriod:
                                    cp52Row.B_PERIOD ||
                                    cp52Row.COMPLAINTS_BILLING_MONTH ||
                                    mappedBillData.billMonth ||
                                    '',
                            })) || raastQr;
                    }
                } catch (qrError) {
                    console.error('[MRC Bill QR] request failed', qrError);
                }

                const mappedWithImages = await expandMrcBillMeterImages(
                    mappedBillData
                );

                if (!isMounted) return;

                setBillData({
                    ...buildEmptyMrcBill(resolvedApplicationId),
                    ...mappedWithImages,
                    raastQr,
                });
            } catch (fetchError) {
                if (!isMounted) return;
                setBillData(null);
                setLoadError(
                    fetchError.message ||
                        'No record found for the specified tracking ID.'
                );
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadBill();

        return () => {
            isMounted = false;
        };
    }, [
        applicationId,
        oracleTrackingId,
        isPostedToIPS,
        token,
        applicationPayableWithinDueDate,
        customerIdFromQuery,
    ]);

    useEffect(() => {
        const titleId = billData?.applicationId || applicationId;
        if (!titleId) return undefined;

        const previousTitle = document.title;
        document.title = titleId;

        return () => {
            document.title = previousTitle;
        };
    }, [billData?.applicationId, applicationId]);

    return (
        <div className="ap-bill-view ap-bill-view--standalone">
            <div className="ap-bill-view__toolbar">
                <div className="ap-bill-view__status">Electricity Consumer Bill</div>
                <div className="ap-bill-view__actions">
                    {downloadError ? (
                        <div className="ap-bill-view__download-error">{downloadError}</div>
                    ) : null}
                    <Button
                        type="button"
                        variant="outlined"
                        className="ap-bill-view__print-btn"
                        onClick={handleDownloadPdf}
                        disabled={!billData || Boolean(loadError) || loading || downloading}
                    >
                        {downloading ? (
                            <AppLoader size={16} />
                        ) : (
                            <DescriptionIcon sx={{ color: '#006ad1', fontSize: '1rem' }} />
                        )}
                        Download PDF
                    </Button>
                    <Button
                        type="button"
                        variant="outlined"
                        className="ap-bill-view__print-btn"
                        onClick={handlePrintBill}
                        disabled={!billData || Boolean(loadError) || loading || downloading}
                    >
                        Print Bill
                    </Button>
                </div>
            </div>
            <div className="ap-bill-view__content">
                {loading ? (
                    <div className="ap-bill-view__loading">
                        <AppLoader size={36} />
                    </div>
                ) : loadError ? (
                    <div className="ap-bill-view__error">{loadError}</div>
                ) : (
                    <MrcElectricityConsumerBill
                        id={BILL_NODE_ID}
                        billData={billData || buildEmptyMrcBill(applicationId)}
                    />
                )}
            </div>
        </div>
    );
};

export default MeterReadingCorrectionBillView;
